import { PencilIcon } from "@heroicons/react/24/solid";
import { useDebounce, useI18N } from "@sundae/react-hooks";
import { Balance, FractionInput, Text, Tooltip, toolkitCx } from "@sundae/ui-toolkit";
import { SundaeUtils } from "@sundaeswap/core/utilities";
import { Fraction } from "@sundaeswap/fraction";
import cx from "classnames";
import isEqual from "lodash/isEqual";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useCustomCompareEffect } from "use-custom-compare";
import { DEFAULT_INPUT_PLACEHOLDER } from "../../../constants/exchange.constants";
import { FRACTION_INPUT_MAX_LENGTH, INPUT_DEBOUNCE_IN_MS } from "../../../constants/page.constants";
import { IEnhancedValue } from "../../../types/Farm.types";
import { numberI18n } from "../../../utils/number-format";
import { useYieldFarmingPositionDialogContext } from "../PositionTable/context";

interface ILockedPositionValueInputProps {
  handleUpdate: (value: string) => void;
  isInputBelowAdaMinLimit: boolean;
  positionValue: IEnhancedValue;
}

const LockedPositionValueInput: FC<ILockedPositionValueInputProps> = ({
  handleUpdate,
  isInputBelowAdaMinLimit,
  positionValue,
}) => {
  const { currentLocale, decimalSeparator, t, thousandSeparator } = useI18N("farms");
  const { insufficientAdaBalance: isAdaBalanceLessThanLimit } =
    useYieldFarmingPositionDialogContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputGreaterThanBalance, setIsInputGreaterThanBalance] = useState(false);
  const [inputEqualsBalance, setInputEqualsBalance] = useState(false);
  const [formattedValue, setFormattedValue] = useState(
    String(
      numberI18n(
        positionValue.assetAmount.value.toNumber(),
        {
          maximumFractionDigits: positionValue.asset.metadata.decimals,
        },
        currentLocale
      )
    )
  );

  const debouncedIsInputBelowAdaMinLimit = useDebounce(isInputBelowAdaMinLimit, {
    wait: INPUT_DEBOUNCE_IN_MS,
  });
  const inputValue = positionValue.assetAmount.value.toString();
  const isAda = SundaeUtils.isAdaAsset(positionValue.asset.metadata);

  /**
   * This function focuses the input and selects the value.
   */
  const handleInputFocus = () => {
    inputRef?.current?.focus();
    inputRef?.current?.select();
  };

  /**
   * Memoized value to check if the debounced input value is below the `MIN_ADA_LIMIT`.
   */
  const showAdaBelowMinLimitWarning = useMemo(
    () => debouncedIsInputBelowAdaMinLimit && isAda,
    [debouncedIsInputBelowAdaMinLimit, isAda]
  );

  /**
   * Memoized value to check if the current ADA balance is below the `MIN_ADA_LIMIT`.
   */
  const showAdaBalanceLessThanLimitWarning = isAda && isAdaBalanceLessThanLimit;

  /**
   * Memoized value to check if the non-ADA input value is zero.
   */
  const isValueZero = useMemo(
    () => positionValue.assetAmount.value.equals(Fraction.ZERO) && !isAda,
    [isAda, positionValue.assetAmount]
  );
  const debouncedIsValueZero = useDebounce(isValueZero, { wait: INPUT_DEBOUNCE_IN_MS });

  const inputTooltipLabel = useMemo(() => {
    if (debouncedIsValueZero) return t("warning.yfLockedPositionZero", { ns: "warnings" });
    if (showAdaBelowMinLimitWarning) return t("warning.yfLockedPositionMinAda", { ns: "warnings" });
    return "";
  }, [debouncedIsValueZero, showAdaBelowMinLimitWarning, t]);

  const adaBalanceTooltipLabel = useMemo(() => {
    if (isAdaBalanceLessThanLimit) return t("warning.yfInsufficientAdaBalance", { ns: "warnings" });
    return "";
  }, [isAdaBalanceLessThanLimit, t]);

  /**
   * This function checks if the input value is greater than the `totalAmount` of the position.
   * If it is, it sets the `isInputGreaterThanBalance` state to `true` and `false` otherwise.
   * The `isInputGreaterThanBalance` state is used to highlight the insufficient balance to the user.
   */
  const checkIsInputGreaterThanBalance = useCallback(
    (value: string) => {
      return positionValue.totalAssetAmount.value.lessThan(value);
    },
    [positionValue.totalAssetAmount]
  );

  /**
   * This function checks if the input value is equal to the `totalAmount` of the position.
   * If it is, it sets the `inputEqualsBalance` state to `true` and `false` otherwise.
   * The `inputEqualsBalance` state is used to toggle the `MAX` button.
   */
  const checkInputEqualsBalance = useCallback(
    (value: string) => {
      return positionValue.totalAssetAmount.value.equals(value);
    },
    [positionValue.totalAssetAmount]
  );

  /**
   * This function is called when the user clicks on the `MAX` button.
   * It sets the value of the input to the `totalAmount` of the position.
   */
  const handleMaxButton = useCallback(() => {
    if (inputEqualsBalance) return;

    const { asset, totalAssetAmount } = positionValue;
    const { decimals } = asset.metadata;
    const i18nValue = numberI18n(
      totalAssetAmount.value.toNumber(),
      { maximumFractionDigits: decimals },
      currentLocale
    );

    handleUpdate(totalAssetAmount.value.toString());
    setFormattedValue(String(i18nValue));
  }, [inputEqualsBalance, positionValue]);

  /**
   * This effect listens for changes on the `totalAmount` value of the position and updates the `equalsBalance`, `isInputZero` or `greaterThanBalance` values.
   */
  useCustomCompareEffect(
    () => {
      setInputEqualsBalance(checkInputEqualsBalance(inputValue));
      setIsInputGreaterThanBalance(checkIsInputGreaterThanBalance(inputValue));
    },
    [positionValue.totalAssetAmount, inputValue],
    (prevDeps, nextDeps) => isEqual(prevDeps, nextDeps)
  );

  return (
    <>
      <Tooltip
        trigger={
          <PencilIcon className="h-3 w-3 cursor-pointer opacity-50" onClick={handleInputFocus} />
        }
        // TODO: i18n + wording
        tooltipLabel="Edit Amount locked"
      />

      <div className="flex items-center gap-1">
        <Tooltip
          open={showAdaBelowMinLimitWarning || debouncedIsValueZero}
          tooltipLabel={inputTooltipLabel}
          trigger={
            <div className="relative max-w-[3.5rem] overflow-hidden md:max-w-[10.5rem]">
              <Text className="invisible inline-block whitespace-pre" size="sm" tag="span">
                {formattedValue}
              </Text>

              <FractionInput
                className={cx(
                  "absolute inset-0 h-6 w-full bg-transparent text-xs md:text-sm",
                  toolkitCx.layout.transition.colors,
                  {
                    [`${toolkitCx.layout.text.default}`]:
                      !showAdaBelowMinLimitWarning || !debouncedIsValueZero,
                    [`!${toolkitCx.layout.text.warning}`]:
                      showAdaBelowMinLimitWarning || debouncedIsValueZero,
                    "motion-safe:animate-shake": isInputGreaterThanBalance,
                  }
                )}
                decimalScale={positionValue.asset.metadata.decimals}
                decimalSeparator={decimalSeparator}
                getInputRef={inputRef}
                onValueChange={({ formattedValue, value }) => {
                  handleUpdate(value);
                  setFormattedValue(formattedValue);
                }}
                maxLength={FRACTION_INPUT_MAX_LENGTH}
                placeholder={DEFAULT_INPUT_PLACEHOLDER}
                thousandSeparator={thousandSeparator}
                value={inputValue}
              />
            </div>
          }
        />

        <Text tag="span" size="sm" variant="silent">
          /
        </Text>
        <Tooltip
          primitiveProps={{
            content: {
              sideOffset: 20,
              className: "z-[100]",
            },
          }}
          open={showAdaBalanceLessThanLimitWarning}
          trigger={
            <div>
              <Balance
                abbreviate={false}
                locale={currentLocale}
                showSymbol={false}
                size="sm"
                value={positionValue.totalAssetAmount}
                variant={
                  isInputGreaterThanBalance || showAdaBalanceLessThanLimitWarning
                    ? "warning"
                    : "silent"
                }
              />
            </div>
          }
          tooltipLabel={adaBalanceTooltipLabel}
        />
      </div>

      <Tooltip
        trigger={
          <div
            className={cx({
              "cursor-pointer": !inputEqualsBalance,
              "cursor-not-allowed select-none": inputEqualsBalance,
            })}
            onClick={handleMaxButton}
          >
            <Text
              role="button"
              size="sm"
              tag="span"
              variant={inputEqualsBalance ? "silent" : "primary"}
            >
              {t("max", { ns: "common" })}
            </Text>
          </div>
        }
        // TODO: i18n + wording
        tooltipLabel="By clicking Max, you will lock all of your balance."
      />
    </>
  );
};

export default LockedPositionValueInput;
